// WhyVoiceFabricPage.js — positive-positioning landing page. Public, no auth.
// Pure "what makes us us" story, no direct competitor comparisons.
// Anchor for LinkedIn ads, podcast appearances, and top-of-funnel referrers
// where a defensive "vs X" tone would feel off.
import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, ShieldCheck, Layers, Zap, MessageSquare, Target, Compass, TrendingUp, MapPin } from 'lucide-react';

const PILLARS = [
  {
    icon: Target,
    title: 'One knowledge base grades every role',
    body: 'Your sales playbook, your support scripts, your accounts collection rules, your reception standards, your manager coaching cadence — all authored in one place, all live-graded against every relevant call. Not five separate tools. Not five separate contracts.',
  },
  {
    icon: MessageSquare,
    title: 'Coaching, not just scoring',
    body: 'Every score is tied to a criterion, a piece of transcript, an approved phrase, and three "evidence calls" — real examples of when the rep already did it right. Managers don\'t decide what to coach. The system tells them. Reps don\'t argue with a number. They listen to their own call.',
  },
  {
    icon: ShieldCheck,
    title: 'Governance without punishment',
    body: 'Compliance breaches escalate independently of the numeric score — a great-sounding call with a missing consent line still triggers the right review. Governance streaks flag repeat offenders in 30 days before they become a legal problem. Every action is audit-logged.',
  },
  {
    icon: TrendingUp,
    title: 'Behaviour change you can prove',
    body: 'The next three calls after a coaching record are auto-tagged. Before/after deltas are captured. Score lift is charted per rep, per criterion, per week. When the CFO asks whether coaching worked, you have the receipts.',
  },
  {
    icon: Compass,
    title: 'Revivable deals surfaced automatically',
    body: 'Every lost deal is analysed for the exact criterion the rep missed, DISC-matched to the caller, ranked by close-probability, and routed to the agent most likely to close it — with a full playbook opening line. The revenue you were leaving on the table becomes a queue.',
  },
  {
    icon: MapPin,
    title: 'Built in Australia, priced for Australian teams',
    body: 'AU-hosted data, AU business-hours support, AU invoicing, priced per seat from A$49/user/month with a 3-seat minimum and month-to-month terms. No US enterprise contract, no 50-seat floor, no US-dollar currency risk.',
  },
];

const OUTCOMES = [
  { metric: '15–30 pt', label: 'Average score lift in 8 weeks' },
  { metric: '100 %', label: 'Of calls graded — no sampling' },
  { metric: '4 wks', label: 'Live from kick-off to first coaching cycle' },
  { metric: '5', label: 'Roles graded on one tenant' },
];

const WhyVoiceFabricPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="why-voicefabric-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="why-vf-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Why Yabbit VoiceFabric</p>
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        One <span className="text-teal-400">Australian platform</span> that turns calls across sales, support, management, administration and accounts into role-specific grading, evidence-based coaching, governance and measurable behaviour change<span className="text-white/60"> — without replacing your Yabbit phone system.</span>
      </h1>
      <p className="text-white/70 text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
        Your team is already on the phone all day. VoiceFabric turns every one of those conversations into a moment
        your business gets better at what it does — with a coaching loop your managers actually have time to run.
      </p>
      <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="why-vf-pricing-cta"
        >
          See pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/free-audit"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="why-vf-audit-cta"
        >
          Free 3-call audit
        </Link>
      </div>
    </section>

    {/* Outcomes strip */}
    <section className="max-w-5xl mx-auto px-6 py-10 border-y border-white/5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        {OUTCOMES.map((o) => (
          <div key={o.label} data-testid={`outcome-${o.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <p className="text-teal-300 text-3xl md:text-4xl font-black">{o.metric}</p>
            <p className="text-white/60 text-xs uppercase tracking-wider mt-2">{o.label}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Six pillars */}
    <section className="max-w-6xl mx-auto px-6 py-20" data-testid="why-vf-pillars">
      <div className="text-center mb-14">
        <h2 className="text-3xl sm:text-4xl font-black text-white">What makes VoiceFabric, VoiceFabric</h2>
        <p className="text-white/50 text-sm mt-3 max-w-2xl mx-auto">
          Six product principles you&apos;ll feel in the first week of using it.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {PILLARS.map((p) => (
          <div key={p.title} className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:border-teal-500/30 transition-colors">
            <p.icon className="w-6 h-6 text-teal-400 mb-4" />
            <h3 className="text-white font-bold text-lg">{p.title}</h3>
            <p className="text-white/70 text-sm mt-2 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Before / After — Monday morning contrast */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="why-vf-before-after">
      <div className="text-center mb-12">
        <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">The Monday-morning test</p>
        <h2 className="text-3xl sm:text-4xl font-black text-white">One question tells you if you need VoiceFabric.</h2>
        <p className="text-white/60 text-sm mt-4 max-w-2xl mx-auto">
          It&apos;s 9am Monday. Your team took 300 calls last week. Which of these is your reality?
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-7">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-red-500/15 text-red-300">Before VoiceFabric</span>
          </div>
          <ul className="space-y-3 text-white/80 text-sm leading-relaxed">
            <li className="flex gap-2"><span className="text-red-400 font-bold shrink-0">×</span>Your best sales rep closed 32%. Your worst closed 11%. You have no idea why.</li>
            <li className="flex gap-2"><span className="text-red-400 font-bold shrink-0">×</span>The 6 recordings your manager listened to Friday afternoon told her nothing about the other 294.</li>
            <li className="flex gap-2"><span className="text-red-400 font-bold shrink-0">×</span>A compliance breach is sitting in a call from Tuesday that nobody will hear until the customer complains.</li>
            <li className="flex gap-2"><span className="text-red-400 font-bold shrink-0">×</span>Coaching in 1:1s is &quot;I felt like your last call was…&quot; — vibes, not evidence.</li>
            <li className="flex gap-2"><span className="text-red-400 font-bold shrink-0">×</span>17 &quot;lost&quot; deals from last month are dead in the CRM. Half of them would&apos;ve closed with a re-approach.</li>
          </ul>
        </div>
        <div className="bg-teal-500/5 border border-teal-500/30 rounded-2xl p-7">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-teal-500/15 text-teal-300">After VoiceFabric</span>
          </div>
          <ul className="space-y-3 text-white/85 text-sm leading-relaxed">
            <li className="flex gap-2"><span className="text-teal-400 font-bold shrink-0">✓</span>Every rep opens their dashboard to <strong className="text-white">the one criterion pulling their score down</strong>, with 3 of their own calls where they nailed it.</li>
            <li className="flex gap-2"><span className="text-teal-400 font-bold shrink-0">✓</span>Managers run 15-minute 1:1s straight off the Coaching Records tab. No prep. No spreadsheet.</li>
            <li className="flex gap-2"><span className="text-teal-400 font-bold shrink-0">✓</span>Tuesday&apos;s compliance breach was flagged Tuesday. Escalated Tuesday. Resolved Tuesday.</li>
            <li className="flex gap-2"><span className="text-teal-400 font-bold shrink-0">✓</span>Feedback is tied to a specific criterion, a specific line in the transcript, an approved phrase from your KB.</li>
            <li className="flex gap-2"><span className="text-teal-400 font-bold shrink-0">✓</span>The Revivable Deals queue has today&apos;s 4 highest-probability re-approaches — DISC-matched, playbook opening line written.</li>
          </ul>
        </div>
      </div>
    </section>


    {/* Story block — the "how the week feels" narrative */}
    <section className="max-w-4xl mx-auto px-6 py-16">
      <div className="bg-gradient-to-br from-teal-500/10 via-transparent to-transparent border border-teal-500/20 rounded-3xl p-10">
        <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">The weekly loop</p>
        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-6">A week inside a VoiceFabric team</h2>
        <div className="space-y-5 text-white/75 leading-relaxed">
          <p>
            <strong className="text-white">Monday morning:</strong> every call from last week has already been graded, tagged, and turned into
            individual coaching cards. Each rep opens their dashboard and sees the one criterion that&apos;s pulling their score down — with
            three of their own calls where they already did it right.
          </p>
          <p>
            <strong className="text-white">Tuesday–Thursday:</strong> managers run 15-minute 1:1s straight off the Coaching Records tab.
            No spreadsheet prep. No listening to random calls hoping for a teachable moment. The system already picked the moment.
          </p>
          <p>
            <strong className="text-white">Friday:</strong> the Revivable Deals queue surfaces the week&apos;s lost opportunities, ranked by
            close-probability, and routes each one to the rep the DISC model says is best-matched to the caller — with a fully-written opening line.
          </p>
          <p>
            <strong className="text-white">End of month:</strong> before/after deltas are automatically captured on every coaching record.
            The team&apos;s score lift is charted, per rep, per criterion. When the CFO asks whether coaching worked, you show the chart.
          </p>
        </div>
      </div>
    </section>

    {/* Customer voice pull-quote — Yabbit Auto Group is an ILLUSTRATIVE composite
        while we secure a real named customer testimonial. Swap when ready. */}
    <section className="max-w-4xl mx-auto px-6 py-16" data-testid="why-vf-quote">
      <figure className="bg-white/5 border border-white/10 rounded-3xl p-10 relative overflow-hidden">
        <div className="absolute -top-4 left-8 text-teal-500/25 text-9xl font-black leading-none select-none pointer-events-none">&ldquo;</div>
        <blockquote className="relative text-white/90 text-xl sm:text-2xl leading-snug font-medium">
          The first Monday after we went live, my sales manager showed me his coaching records tab and said &lsquo;I finally know what to say to Jack about his opener&rsquo;. In eight weeks his team&apos;s conversion score lifted 22 points. No spreadsheets. No guessing.
        </blockquote>
        <figcaption className="mt-6 flex items-center gap-3 text-sm">
          <div className="w-10 h-10 rounded-full bg-teal-500/20 border border-teal-500/40 flex items-center justify-center text-teal-300 font-bold">YA</div>
          <div>
            <div className="text-white font-semibold">General Manager · Yabbit Auto Group</div>
            <div className="text-white/50 text-xs">Multi-site automotive group · 42 phone-facing seats · 8-week rollout · Illustrative example</div>
          </div>
        </figcaption>
      </figure>
    </section>

    {/* Who it's for */}
    <section className="max-w-5xl mx-auto px-6 py-16" data-testid="why-vf-fit">
      <h2 className="text-3xl sm:text-4xl font-black text-white text-center mb-12">Who it&apos;s built for</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            title: 'Phone-heavy Australian SMB',
            body: '10-200 seats, mixed sales/support/admin/accounts, already on Yabbit UCaaS or ready to move. This is the sweet spot — full role coverage, published pricing, month-to-month.',
          },
          {
            title: 'Multi-site operators',
            body: 'Franchise groups, healthcare networks, multi-branch trades. Regional rollups + per-site comparability + centrally-authored KBs — with tenant-level branding for each site.',
          },
          {
            title: 'Resellers & Yabbit partners',
            body: 'White-label the platform under your own brand, add your own consultants on top, and keep a 50% wholesale margin on every seat you sell. VoiceFabric is your voice-intelligence product.',
          },
        ].map((f) => (
          <div key={f.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-bold text-lg mb-2">{f.title}</h3>
            <p className="text-white/60 text-sm leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Risk reversal — makes it psychologically safe to say yes */}
    <section className="max-w-5xl mx-auto px-6 py-16" data-testid="why-vf-risk-reversal">
      <div className="text-center mb-10">
        <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Zero-risk to try</p>
        <h2 className="text-3xl sm:text-4xl font-black text-white">Every reason not to say yes — handled.</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { q: '"What if it doesn\'t work for our industry?"', a: 'Send us three of your own recordings. We\'ll grade them against a role-specific KB for your industry and email you the coaching output — before you commit a dollar. Free 3-call audit.' },
          { q: '"What if we hate it after a month?"', a: 'Month-to-month billing on Grade and Coach. Cancel any time with 30 days\' notice. No annual contract. No termination fee.' },
          { q: '"What if we\'re too small?"', a: '3-seat minimum. A$299/month floor. If you have three people on the phone, VoiceFabric works economically. If you don\'t, we\'ll say so.' },
          { q: '"What if our reps hate being coached by AI?"', a: 'Every score ships with 3 evidence calls where the rep already did it right. Coaching starts with wins, not criticism. Adoption is a design problem — we\'ve solved it.' },
          { q: '"What if it breaks our phone system?"', a: 'It can\'t. VoiceFabric is a coaching layer on top of Yabbit UCaaS — it reads recordings, it doesn\'t touch call routing, numbers, or lines. Zero risk to inbound calls.' },
          { q: '"What if we already have Gong / Dialpad / Aircall?"', a: 'Then we\'re probably not for you — and we\'ll tell you that on the audit call. But if you\'re on Yabbit UCaaS and want the coaching layer without a UCaaS migration, this is what fits.' },
        ].map((o) => (
          <div key={o.q} className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-white font-semibold text-base mb-2">{o.q}</p>
            <p className="text-white/70 text-sm leading-relaxed">{o.a}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-20 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Start with three of your own calls.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          We&apos;ll grade them against a role-specific knowledge base for your industry and send back the exact coaching card each rep would see in the platform.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="why-vf-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">AU-hosted · tenant-isolated · Privacy Act 2026 ready</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3</p>
        </div>
        <div>
          <Layers className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">5-role grading</h3>
          <p className="text-white/50 text-sm mt-1">Sales, support, admin, accounts, management — one platform</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/vs-dialpad" className="hover:text-white/50">vs Dialpad</Link> · <Link to="/vs-aircall" className="hover:text-white/50">vs Aircall</Link>
    </footer>
  </div>
);

export default WhyVoiceFabricPage;
